import { useCallback, useEffect, useRef, useState } from 'react';

const HIDE_DELAY = 3000;

export default function useControlsVisibility(isPlaying: boolean): {
  visible: boolean;
  onInteraction: () => void;
  onControlsMouseEnter: () => void;
  onControlsMouseLeave: () => void;
} {
  const [visible, setVisible] = useState(true);
  const timeoutRef = useRef<number>(0);
  const isOverControls = useRef(false);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = 0;
    }
  }, []);

  const startHideTimer = useCallback(() => {
    clearTimer();
    if (isPlaying && !isOverControls.current) {
      timeoutRef.current = window.setTimeout(() => {
        setVisible(false);
      }, HIDE_DELAY);
    }
  }, [isPlaying, clearTimer]);

  const onInteraction = useCallback(() => {
    setVisible(true);
    startHideTimer();
  }, [startHideTimer]);

  const onControlsMouseEnter = useCallback(() => {
    isOverControls.current = true;
    clearTimer();
  }, [clearTimer]);

  const onControlsMouseLeave = useCallback(() => {
    isOverControls.current = false;
    startHideTimer();
  }, [startHideTimer]);

  // Show controls when paused, start timer when playing
  useEffect(() => {
    if (!isPlaying) {
      clearTimer();
      setVisible(true);
    } else {
      startHideTimer();
    }
  }, [isPlaying, clearTimer, startHideTimer]);

  // Cleanup
  useEffect(() => {
    return () => clearTimer();
  }, [clearTimer]);

  return { visible, onInteraction, onControlsMouseEnter, onControlsMouseLeave };
}
